import React, { useEffect, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Link as LinkIcon,
  RefreshCw,
  Save,
  ShieldCheck
} from 'lucide-react';

const DEFAULTS = {
  enabled: false,
  providerName: 'Single Sign-On',
  issuerUrl: '',
  clientId: '',
  clientSecret: '',
  tokenAuthMethod: 'client_secret_post',
  scopes: 'openid profile email',
  emailClaim: 'email',
  groupsClaim: 'groups',
  ownerGroup: '',
  autoProvision: true,
  defaultRole: 'member',
  allowLocalLogin: true,
  localLoginEnabled: true,
  localLoginManagedByDocker: false,
  hasClientSecret: false,
  redirectUri: '',
  linked: false
};

function ToggleRow({ label, description, checked, onChange, disabled = false }) {
  return (
    <div className="flex items-start sm:items-center justify-between gap-5 py-1">
      <div className="min-w-0 pr-2">
        <div className="text-sm font-medium text-text-primary dark:text-text-primary-dark">{label}</div>
        {description && <div className="text-xs leading-relaxed text-text-muted dark:text-text-muted-dark mt-1">{description}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-action dark:bg-action-dark' : 'bg-border dark:bg-surface-dark'}`}
        aria-pressed={checked}
        aria-label={label}
      >
        <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder = '', disabled = false, help = '' }) {
  return (
    <div className="min-w-0">
      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full min-w-0 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
      />
      {help && <p className="text-xs leading-relaxed text-text-muted dark:text-text-muted-dark mt-1">{help}</p>}
    </div>
  );
}

function SectionHeading({ title, description }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">{title}</h4>
      {description && <p className="text-xs leading-relaxed text-text-muted dark:text-text-muted-dark mt-1">{description}</p>}
    </div>
  );
}

export default function OidcSettings({ apiCall, showAlert }) {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const load = async () => {
    try {
      const res = await apiCall('/api/admin/auth/oidc');
      if (!res.ok) throw new Error('Failed to load OIDC settings');
      const data = await res.json();
      setConfig({ ...DEFAULTS, ...data, clientSecret: '' });
      setDirty(false);
    } catch (err) {
      if (showAlert) showAlert(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('oidc_status') === 'linked') {
      setIsOpen(true);
      if (showAlert) showAlert('OIDC identity linked successfully.', 'success');
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const update = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const payload = () => ({
    enabled: config.enabled,
    providerName: config.providerName,
    issuerUrl: config.issuerUrl,
    clientId: config.clientId,
    clientSecret: config.clientSecret,
    tokenAuthMethod: config.tokenAuthMethod,
    scopes: config.scopes,
    emailClaim: config.emailClaim,
    groupsClaim: config.groupsClaim,
    ownerGroup: config.ownerGroup,
    autoProvision: config.autoProvision,
    defaultRole: config.defaultRole,
    allowLocalLogin: config.allowLocalLogin
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiCall('/api/admin/auth/oidc', { method: 'POST', body: JSON.stringify(payload()) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save OIDC settings');
      setConfig(prev => ({ ...prev, ...data, clientSecret: '' }));
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      if (showAlert) showAlert(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const testDiscovery = async () => {
    setTesting(true);
    try {
      const res = await apiCall('/api/admin/auth/oidc/test', { method: 'POST', body: JSON.stringify(payload()) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'OIDC discovery failed');
      if (showAlert) showAlert(`Discovery succeeded for ${data.issuer}${data.supportsPKCE ? ' (PKCE supported)' : ''}.`, 'success');
    } catch (err) {
      if (showAlert) showAlert(err.message, 'error');
    } finally {
      setTesting(false);
    }
  };

  const canLink = !dirty && config.enabled && config.issuerUrl && config.clientId;

  if (loading) {
    return <div className="p-4 text-sm text-text-muted dark:text-text-muted-dark">Loading authentication settings...</div>;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="w-full flex items-center justify-between gap-4 p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
      >
        <div className="flex items-start gap-3 min-w-0 text-left">
          <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0 text-action dark:text-action-dark" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">OpenID Connect</h2>
            <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Configure Single Sign-On and automatic user provisioning.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`hidden sm:inline-flex text-xs px-2 py-1 rounded-badge ${config.enabled ? 'bg-success-bg dark:bg-success-bg-dark text-success-text dark:text-success-text-dark' : 'bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark'}`}>
            {config.enabled ? 'Enabled' : 'Disabled'}
          </span>
          {isOpen ? <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" /> : <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />}
        </div>
      </button>

      {isOpen && (
        <div className="mt-4 bg-surface dark:bg-card-dark/50 rounded-input p-4 sm:p-5 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm leading-relaxed text-text-secondary dark:text-text-muted-dark max-w-xl">
              Use any standards-compliant OIDC provider. Swiish keeps its existing local roles and sessions after sign-in.
            </p>
            <span className={`text-xs px-2 py-1 rounded-badge whitespace-nowrap ${config.linked ? 'bg-success-bg dark:bg-success-bg-dark text-success-text dark:text-success-text-dark' : 'bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark'}`}>
              {config.linked ? 'Linked' : 'Not linked'}
            </span>
          </div>

          <ToggleRow
            label="Enable OpenID Connect"
            description="Show this provider on the Swiish login page."
            checked={config.enabled}
            onChange={v => update('enabled', v)}
          />

          <div className="border-t border-border dark:border-border-dark pt-5 space-y-4">
            <SectionHeading title="Provider" description="Connection details from your identity provider." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Provider name" value={config.providerName} onChange={v => update('providerName', v)} placeholder="Hirose SSO" />
              <Field label="Issuer URL" value={config.issuerUrl} onChange={v => update('issuerUrl', v)} placeholder="https://auth.example.com/application/o/swiish/" />
              <Field label="Client ID" value={config.clientId} onChange={v => update('clientId', v)} />
              <div className="min-w-0">
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Token endpoint authentication</label>
                <select value={config.tokenAuthMethod} onChange={e => update('tokenAuthMethod', e.target.value)} className="w-full min-w-0 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark">
                  <option value="client_secret_post">Client secret POST</option>
                  <option value="client_secret_basic">Client secret Basic</option>
                  <option value="none">Public client (no secret)</option>
                </select>
              </div>
              <Field
                label="Client secret"
                type="password"
                value={config.clientSecret}
                onChange={v => update('clientSecret', v)}
                disabled={config.tokenAuthMethod === 'none'}
                placeholder={config.hasClientSecret ? 'Stored — leave blank to keep' : 'Client secret'}
                help="Stored securely server-side and never returned to the browser."
              />
              <Field label="Scopes" value={config.scopes} onChange={v => update('scopes', v)} placeholder="openid profile email" />
            </div>

            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Redirect URI</label>
              <div className="flex items-start gap-2 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-sm font-mono text-text-secondary dark:text-text-muted-dark break-all">
                <LinkIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{config.redirectUri}</span>
              </div>
              <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Register this exact URI in your OIDC provider.</p>
            </div>
          </div>

          <div className="border-t border-border dark:border-border-dark pt-5 space-y-4">
            <SectionHeading title="Identity & role mapping" description="Choose the claims Swiish uses for account identity and role assignment." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email claim" value={config.emailClaim} onChange={v => update('emailClaim', v)} placeholder="email" />
              <Field label="Groups claim" value={config.groupsClaim} onChange={v => update('groupsClaim', v)} placeholder="groups" />
              <Field label="Owner group" value={config.ownerGroup} onChange={v => update('ownerGroup', v)} placeholder="Swiish Owners" help="Exact group match. Leave blank to use the default role for everyone." />
              <div className="min-w-0">
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Default provisioned role</label>
                <select value={config.defaultRole} onChange={e => update('defaultRole', e.target.value)} className="w-full min-w-0 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark">
                  <option value="member">Member</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
            </div>
            <ToggleRow
              label="Auto-provision users"
              description="Create a local Swiish user on first successful OIDC login."
              checked={config.autoProvision}
              onChange={v => update('autoProvision', v)}
            />
          </div>

          <div className="border-t border-border dark:border-border-dark pt-5 space-y-4">
            <SectionHeading title="Access" description="Control fallback access while you roll out SSO." />
            <ToggleRow
              label="Allow local password login"
              description={config.localLoginManagedByDocker ? 'Managed by LOCAL_LOGIN_ENABLED in Docker Compose.' : 'Keep normal password login available as a fallback for local accounts.'}
              checked={config.localLoginManagedByDocker ? config.localLoginEnabled : config.allowLocalLogin}
              onChange={v => update('allowLocalLogin', v)}
              disabled={config.localLoginManagedByDocker}
            />
          </div>

          <div className="border-t border-border dark:border-border-dark pt-5">
            <p className="text-xs leading-relaxed text-text-muted dark:text-text-muted-dark mb-4">
              Test Discovery can use unsaved values. Save the configuration before linking your account.
            </p>
            <div className="grid grid-cols-1 sm:flex gap-2">
              <button type="button" onClick={testDiscovery} disabled={testing} className="px-4 py-2.5 rounded-full text-sm font-medium bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark flex items-center justify-center gap-2 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} /> {testing ? 'Testing...' : 'Test Discovery'}
              </button>
              <button
                type="button"
                onClick={() => { window.location.href = '/api/auth/oidc/link/start'; }}
                disabled={!canLink}
                title={!canLink ? 'Save an enabled OIDC configuration before linking.' : undefined}
                className="px-4 py-2.5 rounded-full text-sm font-medium bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ExternalLink className="w-4 h-4" /> {config.linked ? 'Relink My Account' : 'Link My Account'}
              </button>
              <button type="button" onClick={save} disabled={saving} className="sm:ml-auto px-5 py-2.5 rounded-full text-sm font-bold bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark flex items-center justify-center gap-2 disabled:opacity-50">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save OIDC'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
